/**
 * Archived Jobs page — shows all jobs in "archived" status.
 * Click a row to open the job detail.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { fetchArchivedJobs } from '../api/jobs';
import { LoadingSpinner, EmptyState, SearchBar, Pagination } from '../components/common';

function fmtDate(d) {
  if (!d) return '—';
  try { return format(parseISO(d), 'MMM d, yyyy'); } catch { return d; }
}

function fmtCurrency(v) {
  if (v == null || v === '' || v === undefined) return '—';
  const n = parseFloat(v);
  if (isNaN(n)) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

export default function ArchivedJobsPage() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const PAGE_SIZE = 25;

  const loadJobs = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await fetchArchivedJobs({
        page,
        page_size: PAGE_SIZE,
        search: search || undefined,
      });
      // Handles both {data: [...]} and {count, results: [...]}
      const results = resp?.data || resp?.results || resp || [];
      const count = resp?.count ?? (Array.isArray(results) ? results.length : 0);
      setJobs(Array.isArray(results) ? results : []);
      setTotalCount(count);
    } catch {
      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => { loadJobs(); }, [loadJobs]);

  // Reset page when search changes
  useEffect(() => { setPage(1); }, [search]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  return (
    <div>
      {/* Page header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Archived Jobs</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Completed, invoiced, and archived jobs — included in P&L reporting.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <SearchBar
            value={search}
            onChange={(v) => setSearch(v)}
            placeholder="Search job #, customer, part…"
            className="w-64"
          />
        </div>
      </div>

      {/* Table card */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16">
            <LoadingSpinner />
          </div>
        ) : jobs.length === 0 ? (
          <EmptyState
            title={search ? 'No archived jobs match your search' : 'No archived jobs yet'}
            description={
              search
                ? 'Try a different search term.'
                : 'Jobs appear here after they are invoiced and archived.'
            }
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wider border-b border-gray-200">
                    <th className="px-4 py-3">Job #</th>
                    <th className="px-4 py-3">Customer</th>
                    <th className="px-4 py-3">Parent Item</th>
                    <th className="px-4 py-3">Qty</th>
                    <th className="px-4 py-3">Invoice #</th>
                    <th className="px-4 py-3">Invoice Date</th>
                    <th className="px-4 py-3 text-right">Ext Sell Price</th>
                    <th className="px-4 py-3 text-right">True Cost</th>
                    <th className="px-4 py-3 text-right">Margin</th>
                    <th className="px-4 py-3">Archived</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {jobs.map((job) => {
                    const sellTotal = parseFloat(job.sell_price || 0) * parseInt(job.quantity || 1, 10);
                    const trueCost = parseFloat(job.true_cost || 0);
                    const margin = sellTotal && trueCost ? sellTotal - trueCost : null;
                    const marginPct = margin != null && sellTotal ? (margin / sellTotal * 100).toFixed(1) : null;
                    const marginPositive = margin != null && margin >= 0;

                    return (
                      <tr
                        key={job.id}
                        onClick={() => navigate(`/jobs/${job.id}`)}
                        className="hover:bg-blue-50 cursor-pointer transition-colors"
                      >
                        <td className="px-4 py-3 font-mono text-sm font-semibold text-blue-700">
                          {job.job_number}
                        </td>
                        <td className="px-4 py-3 font-medium text-foreground">
                          {job.customer_name || job.customer?.name || '—'}
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-mono text-xs bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded">
                            {job.parent_item_no || job.parent_item?.parent_item_no || '—'}
                          </span>
                          {(job.parent_item_description || job.parent_item?.description) && (
                            <span className="ml-2 text-gray-500 text-xs truncate max-w-[180px] inline-block align-middle">
                              {job.parent_item_description || job.parent_item?.description}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center text-gray-600">{job.quantity ?? '—'}</td>
                        <td className="px-4 py-3 font-mono text-xs text-gray-600">{job.invoice_number || '—'}</td>
                        <td className="px-4 py-3 text-gray-600">{fmtDate(job.invoice_date)}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="font-medium">{fmtCurrency(sellTotal || null)}</div>
                          {job.quantity > 1 && (
                            <div className="text-[10px] text-gray-400">{job.quantity} × {fmtCurrency(job.sell_price)}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-600">{fmtCurrency(job.true_cost)}</td>
                        <td className="px-4 py-3 text-right">
                          {margin != null ? (
                            <span className={marginPositive ? 'text-green-700 font-medium' : 'text-red-600 font-medium'}>
                              {fmtCurrency(margin)}
                              {marginPct && (
                                <span className="ml-1 text-xs font-normal opacity-70">({marginPct}%)</span>
                              )}
                            </span>
                          ) : '—'}
                        </td>
                        <td className="px-4 py-3 text-gray-500 text-xs">
                          {fmtDate(job.archived_at)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
              <p className="text-xs text-gray-500">
                {totalCount} archived job{totalCount !== 1 ? 's' : ''}
                {search && ` matching "${search}"`}
              </p>
              <Pagination current={page} total={totalPages} onChange={setPage} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
